import { useEffect, useLayoutEffect, useRef } from "react";

const CODE_LENGTH = 4;
const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

// Keep four visible boxes, but accept an entire SMS code in any one of them.
const SmsCodeInput = ({ onChange, onClick, autoFocus = true }) => {
  const inputsRef = useRef([]);
  const containerRef = useRef(null);
  const lastCellsRef = useRef(Array(CODE_LENGTH).fill(""));

  useEffect(() => {
    if (autoFocus && inputsRef.current[0]) inputsRef.current[0].focus();
  }, [autoFocus]);

  const focusInput = (index) => {
    const input = inputsRef.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  };

  const publishValue = () => {
    const cells = inputsRef.current.map((input) => input.value);
    const value = cells.join("");
    const previousValue = lastCellsRef.current.join("");
    lastCellsRef.current = cells;
    // Browsers may emit both input and change for the same autofill operation.
    if (value !== previousValue) {
      if (onChange) onChange(value);
    }
  };

  const applyValue = (index, rawValue) => {
    const value = digitsOnly(rawValue).slice(0, CODE_LENGTH);
    if (value.length <= 1 && value === lastCellsRef.current[index]) {
      inputsRef.current[index].value = value;
      return;
    }
    if (value.length > 1) {
      // Autofill/paste replaces the code, including any previously entered digits.
      inputsRef.current.forEach((input, position) => {
        input.value = value[position] || "";
      });
      focusInput(Math.min(value.length, CODE_LENGTH - 1));
    } else {
      inputsRef.current[index].value = value;
      if (value) focusInput(Math.min(index + 1, CODE_LENGTH - 1));
    }
    publishValue();
  };

  const onKeyDown = (event, index) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      const targetIndex = event.currentTarget.value || index === 0 ? index : index - 1;
      inputsRef.current[targetIndex].value = "";
      focusInput(targetIndex);
      publishValue();
    } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
      focusInput(Math.max(0, Math.min(CODE_LENGTH - 1, index + (event.key === "ArrowLeft" ? -1 : 1))));
    }
  };

  // Preact compat maps JSX onChange to input; listen to native change as well
  // for autofill providers that only dispatch change.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const handleChange = (event) => {
      const index = inputsRef.current.indexOf(event.target);
      if (index >= 0) applyValue(index, event.target.value);
    };
    const handlePaste = (event) => {
      const index = inputsRef.current.indexOf(event.target);
      if (index < 0 || !event.clipboardData) return;
      event.preventDefault();
      const value = digitsOnly(event.clipboardData.getData("text"));
      if (value) applyValue(index, value);
    };
    container.addEventListener("change", handleChange);
    container.addEventListener("paste", handlePaste);
    return () => {
      container.removeEventListener("change", handleChange);
      container.removeEventListener("paste", handlePaste);
    };
  });

  return (
    <div ref={containerRef} onClick={onClick}>
      {Array.from({ length: CODE_LENGTH }, (_, index) => (
        <input
          key={index}
          ref={(input) => { inputsRef.current[index] = input; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={CODE_LENGTH}
          aria-label={`Verification code digit ${index + 1}`}
          onInput={(event) => applyValue(index, event.currentTarget.value)}
          onFocus={(event) => event.currentTarget.select()}
          onKeyDown={(event) => onKeyDown(event, index)}
        />
      ))}
    </div>
  );
};

export default SmsCodeInput;
